// Season Wrapped share-image export.
//
// Captures the off-screen ShareCard DOM node to a PNG, then hands it off:
//   NATIVE (iOS/Android): write the PNG to the cache dir and open the OS share
//     sheet via @capacitor/share (so it lands in Messages / Instagram / etc).
//   WEB: use the Web Share API with a File when available; otherwise download.
//
// Best-effort: every failure is swallowed by the caller. The share button never
// blocks the Wrapped experience.

import { toPng } from 'html-to-image';
import { Capacitor } from '@capacitor/core';
import type { SeasonWrappedData } from '../../types/dugout';

function fileName(data: SeasonWrappedData): string {
  const safe = (data.teamName || 'team').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  return `${safe}-${data.seasonYear}-wrapped.png`;
}

async function captureDataUrl(node: HTMLElement): Promise<string> {
  // pixelRatio 2 → crisp on retina / social. backgroundColor matches the card.
  return toPng(node, { pixelRatio: 2, backgroundColor: '#0D0D0D', cacheBust: true });
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [meta, b64] = dataUrl.split(',');
  const mime = (meta.match(/data:(.*?);/) || [])[1] || 'image/png';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** Capture + share the Wrapped card. Resolves when handed off (or downloaded). */
export async function shareWrappedCard(node: HTMLElement, data: SeasonWrappedData): Promise<void> {
  const dataUrl = await captureDataUrl(node);
  const name = fileName(data);
  const title = `${data.teamName} ${data.seasonYear} Wrapped`;
  const text = `${data.teamName}: ${data.record.wins}-${data.record.losses} this season. ${data.thesis}`;

  if (Capacitor.isNativePlatform()) {
    // Write to cache, then open the native share sheet with the file URI.
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const { Share } = await import('@capacitor/share');
    const base64 = dataUrl.split(',')[1];
    const writeRes = await Filesystem.writeFile({
      path: name,
      data: base64,
      directory: Directory.Cache,
    });
    await Share.share({ title, text, url: writeRes.uri, dialogTitle: 'Share your Wrapped' });
    return;
  }

  // Web: prefer native Web Share with a File (mobile browsers), else download.
  const blob = dataUrlToBlob(dataUrl);
  const file = new File([blob], name, { type: 'image/png' });
  const nav = navigator as any;
  if (nav.canShare && nav.canShare({ files: [file] })) {
    try {
      await nav.share({ files: [file], title, text });
      return;
    } catch {
      /* user cancelled or share failed → fall through to download */
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
